"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence, useReducedMotion, type Variants, type PanInfo } from "motion/react";
import { useRouter, usePathname } from "next/navigation";
import { cn } from "@/util/cn";
import { usePortfolioOpen } from "@/hooks/modals/usePortfolioOpen";
import { drawerCategories, springTransition, type NavSubItem } from "./mobile-nav-config";
import { Link } from "@heroui/react";
import { footerNavigation } from "components/Footer";
interface MobileNavDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  triggerRef: React.RefObject<HTMLButtonElement | null>;
}

// Threshold for closing drawer (in pixels)
const CLOSE_THRESHOLD = 80;
// Velocity threshold for quick flick to close (px/s)
const VELOCITY_THRESHOLD = 300;
export function MobileNavDrawer(t0) {
  const $ = _c(39);
  const {
    isOpen,
    onClose,
    triggerRef
  } = t0;
  const router = useRouter();
  const pathname = usePathname();
  const prefersReducedMotion = useReducedMotion();
  const firstItemRef = useRef(null);
  const drawerRef = useRef(null);
  const {
    onOpen: onPortfolioOpen
  } = usePortfolioOpen();
  let t1;
  let t2;
  if ($[0] !== isOpen) {
    t1 = () => {
      if (isOpen) {
        const timer = setTimeout(() => {
          firstItemRef.current?.focus();
        }, 100);
        return () => clearTimeout(timer);
      }
    };
    t2 = [isOpen];
    $[0] = isOpen;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  if ($[3] !== isOpen || $[4] !== onClose || $[5] !== triggerRef) {
    t3 = e => {
      if (!isOpen || !drawerRef.current) {
        return;
      }
      if (e.key === "Escape") {
        onClose();
        triggerRef.current?.focus();
        return;
      }
      if (e.key === "Tab") {
        const focusableElements = drawerRef.current.querySelectorAll("button, [href], input, select, textarea, [tabindex]:not([tabindex=\"-1\"])");
        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];
        if (e.shiftKey && document.activeElement === firstElement) {
          e.preventDefault();
          lastElement?.focus();
        } else {
          if (!e.shiftKey && document.activeElement === lastElement) {
            e.preventDefault();
            firstElement?.focus();
          }
        }
      }
    };
    $[3] = isOpen;
    $[4] = onClose;
    $[5] = triggerRef;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const handleKeyDown = t3;
  let t4;
  let t5;
  if ($[7] !== handleKeyDown) {
    t4 = () => {
      window.addEventListener("keydown", handleKeyDown);
      return () => window.removeEventListener("keydown", handleKeyDown);
    };
    t5 = [handleKeyDown];
    $[7] = handleKeyDown;
    $[8] = t4;
    $[9] = t5;
  } else {
    t4 = $[8];
    t5 = $[9];
  }
  useEffect(t4, t5);
  let t6;
  let t7;
  if ($[10] !== isOpen) {
    t6 = () => {
      if (isOpen) {
        document.body.style.overflow = "hidden";
      } else {
        document.body.style.overflow = "";
      }
      return _temp;
    };
    t7 = [isOpen];
    $[10] = isOpen;
    $[11] = t6;
    $[12] = t7;
  } else {
    t6 = $[11];
    t7 = $[12];
  }
  useEffect(t6, t7);
  let t8;
  if ($[13] !== onClose || $[14] !== triggerRef) {
    t8 = (_event, info) => {
      const {
        offset,
        velocity
      } = info;
      const shouldClose = offset.y > CLOSE_THRESHOLD || velocity.y > VELOCITY_THRESHOLD && offset.y > 20;
      if (shouldClose) {
        onClose();
        triggerRef.current?.focus();
      }
    };
    $[13] = onClose;
    $[14] = triggerRef;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const handleDragEnd = t8;
  let T0;
  let t9;
  if ($[16] !== handleDragEnd || $[17] !== isOpen || $[18] !== onClose || $[19] !== onPortfolioOpen || $[20] !== pathname || $[21] !== prefersReducedMotion || $[22] !== router || $[23] !== triggerRef) {
    const handleItemPress = item => {
      if (item.action) {
        if (item.action.type === "portfolio") {
          onPortfolioOpen(item.action.tab);
        } else {
          if (item.action.type === "external") {
            window.open(item.action.url, "_blank", "noopener,noreferrer");
          }
        }
      } else {
        if (item.href) {
          router.push(item.href);
        }
      }
      onClose();
      setTimeout(() => {
        triggerRef.current?.focus();
      }, 100);
    };
    let t10;
    if ($[26] !== pathname) {
      t10 = href => {
        if (!href) {
          return false;
        }
        if (href === "/") {
          return pathname === "/";
        }
        return pathname === href || pathname.startsWith(href + "/");
      };
      $[26] = pathname;
      $[27] = t10;
    } else {
      t10 = $[27];
    }
    const isItemActive = t10;
    let t11;
    if ($[28] !== prefersReducedMotion) {
      t11 = prefersReducedMotion ? {
        initial: {
          opacity: 0
        },
        animate: {
          opacity: 1
        },
        exit: {
          opacity: 0
        }
      } : {
        initial: {
          opacity: 0
        },
        animate: {
          opacity: 1
        },
        exit: {
          opacity: 0,
          transition: {
            delay: 0.1
          }
        }
      };
      $[28] = prefersReducedMotion;
      $[29] = t11;
    } else {
      t11 = $[29];
    }
    const backdropVariants = t11;
    let t12;
    if ($[30] !== prefersReducedMotion) {
      t12 = prefersReducedMotion ? {
        initial: {
          opacity: 0
        },
        animate: {
          opacity: 1
        },
        exit: {
          opacity: 0
        }
      } : {
        initial: {
          y: "100%",
          opacity: 1
        },
        animate: {
          y: 0,
          opacity: 1,
          transition: springTransition
        },
        exit: {
          y: "100%",
          opacity: 1,
          transition: springTransition
        }
      };
      $[30] = prefersReducedMotion;
      $[31] = t12;
    } else {
      t12 = $[31];
    }
    const drawerVariants = t12;
    let t13;
    if ($[32] !== prefersReducedMotion) {
      t13 = prefersReducedMotion ? {
        initial: {
          opacity: 1
        },
        animate: {
          opacity: 1
        },
        exit: {
          opacity: 0
        }
      } : {
        initial: {
          opacity: 0,
          y: 10
        },
        animate: {
          opacity: 1,
          y: 0
        },
        exit: {
          opacity: 0,
          y: 10
        }
      };
      $[32] = prefersReducedMotion;
      $[33] = t13;
    } else {
      t13 = $[33];
    }
    const categoryVariants = t13;
    let t14;
    if ($[34] !== prefersReducedMotion) {
      t14 = prefersReducedMotion ? {
        initial: {
          opacity: 1
        },
        animate: {
          opacity: 1
        },
        exit: {
          opacity: 0
        }
      } : {
        initial: {
          opacity: 0,
          x: 10
        },
        animate: {
          opacity: 1,
          x: 0
        },
        exit: {
          opacity: 0,
          x: -10
        }
      };
      $[34] = prefersReducedMotion;
      $[35] = t14;
    } else {
      t14 = $[35];
    }
    const itemVariants = t14;
    T0 = AnimatePresence;
    t9 = isOpen && <><motion.div className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm" variants={backdropVariants} initial="initial" animate="animate" exit="exit" aria-hidden="true" onClick={onClose} style={{
        bottom: "calc(72px + env(safe-area-inset-bottom))"
      }} /><motion.div ref={drawerRef} id="app-drawer" role="dialog" aria-modal="true" aria-label="Navigation menu" className="fixed left-0 right-0 z-40 bg-background/80 backdrop-blur-xl border-t border-white/10 rounded-t-3xl overflow-hidden touch-none" style={{
        bottom: "calc(71px + env(safe-area-inset-bottom))",
        maxHeight: "95vh"
      }} variants={drawerVariants} initial="initial" animate="animate" exit="exit" drag="y" dragConstraints={{
        top: 0,
        bottom: 0
      }} dragElastic={{
        top: 0,
        bottom: 0.5
      }} dragMomentum={true} dragTransition={{
        bounceStiffness: 300,
        bounceDamping: 20
      }} onDragEnd={handleDragEnd}><span className="sr-only" aria-live="polite">Navigation menu open. {drawerCategories.length} categories available.</span><div className="flex justify-center pt-3 pb-6"><div className="w-20 h-1 bg-white/10 rounded-full" aria-hidden="true" /></div><div className="overflow-y-auto overscroll-contain px-4 pb-4 h-fit" style={{
          maxHeight: "calc(95vh - 20px)"
        }}><motion.div className="space-y-6" initial="initial" animate="animate" exit="exit" variants={{
            animate: {
              transition: {
                staggerChildren: 0.05
              }
            }
          }}>{drawerCategories.map((category, categoryIndex) => <motion.div key={category.id} variants={categoryVariants}><div className="flex flex-row justify-between"><h3 className="text-xs font-semibold text-secondary uppercase tracking-wider mb-3 px-1">{category.label}</h3>{category.description && <h3 className="ml-2 text-xs text-default-500/60 uppercase font-italic flex text-right mb-3 text-center">{category.description}</h3>}</div><motion.div className="grid grid-cols-2 gap-2" variants={{
                animate: {
                  transition: {
                    staggerChildren: 0.03
                  }
                }
              }}>{category.items.map((item_0, itemIndex) => {
                  const Icon = item_0.icon;
                  const active = isItemActive(item_0.href);
                  const isFirstItem = categoryIndex === 0 && itemIndex === 0;
                  return <motion.button key={item_0.href ?? item_0.label} ref={isFirstItem ? firstItemRef : undefined} variants={itemVariants} whileTap={{
                    scale: 0.95
                  }} onClick={() => handleItemPress(item_0)} className={cn("flex items-center gap-3 p-3 rounded-xl transition-colors min-h-[44px]", "focus:outline-none focus-visible:ring-2 focus-visible:ring-secondary focus-visible:ring-offset-2 focus-visible:ring-offset-background", active ? "bg-secondary/15 text-secondary" : "bg-white/5 text-default-600 hover:bg-white/10 hover:text-white")}><Icon className="h-5 w-5 shrink-0" aria-hidden="true" /><span className="text-sm font-medium truncate">{item_0.label}</span></motion.button>;
                })}</motion.div></motion.div>)}<div className="flex flex-row space-x-6 justify-center pb-2">{footerNavigation.social.map(_temp2)}</div></motion.div></div></motion.div></>;
    $[16] = handleDragEnd;
    $[17] = isOpen;
    $[18] = onClose;
    $[19] = onPortfolioOpen;
    $[20] = pathname;
    $[21] = prefersReducedMotion;
    $[22] = router;
    $[23] = triggerRef;
    $[24] = T0;
    $[25] = t9;
  } else {
    T0 = $[24];
    t9 = $[25];
  }
  let t10;
  if ($[36] !== T0 || $[37] !== t9) {
    t10 = <T0>{t9}</T0>;
    $[36] = T0;
    $[37] = t9;
    $[38] = t10;
  } else {
    t10 = $[38];
  }
  return t10;
}
function _temp2(item_1) {
  return <Link key={item_1.name} isExternal={true} className="text-default-400" href={item_1.href}><span className="sr-only">{item_1.name}</span><item_1.icon aria-hidden="true" width="25px" height="25px" className="" /></Link>;
}
function _temp() {
  document.body.style.overflow = "";
}